import clsx from 'clsx';

import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';

import type { ReactElement, ReactNode } from 'react';

export interface StatusIndicatorProps {
	/** The primary text. */
	label: ReactNode;
	/** Qualifier shown after the label, behind a separator. */
	suffix?: ReactNode;
	/** Colour class for the leading dot. Omit to render no dot. */
	dotClassName?: string;
	/** Pulse the dot, marking a state that is moving rather than waiting. */
	pulse?: boolean;
	/** Colour class for the label. */
	labelClassName?: string;
	/** Colour class for the suffix. */
	suffixClassName?: string;
	/** Lay the line out right to left, for a slot anchored to the right edge. */
	reversed?: boolean;

	className?: string;
}

/**
 * A state as one line: an optional coloured dot, a label, and an optional
 * qualifier behind a separator. Carries no vocabulary of its own — the caller
 * resolves what the state is called and which colours it wears. Sized to the
 * surrounding text.
 */
export function StatusIndicator({
	label,
	suffix,
	dotClassName,
	pulse = false,
	labelClassName,
	suffixClassName,
	reversed = false,
	className,
}: StatusIndicatorProps): ReactElement {
	const el = useDataElTagGenerator(StatusIndicator);
	return (
		<span
			{...el()}
			className={clsx('flex items-center gap-1.5 whitespace-nowrap', reversed && 'flex-row-reverse', className)}
		>
			{dotClassName !== undefined && (
				<span className={clsx('h-2 w-2 shrink-0 rounded-full', dotClassName, pulse && 'animate-pulse')} />
			)}
			<span className={labelClassName}>{label}</span>
			{suffix !== undefined && (
				<>
					<span aria-hidden className='text-gray-a6'>
						·
					</span>
					<span className={suffixClassName}>{suffix}</span>
				</>
			)}
		</span>
	);
}
